import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Waves,
  Flame,
  Trees,
  Car,
  Umbrella,
  Dumbbell,
  Coffee,
  Lock,
  Accessibility,
  Check,
  Euro,
  X,
  ChevronDown,
  ChevronUp,
  Building,
  Sparkles,
  type LucideIcon,
  // Pool & Wellness
  Bath,
  Thermometer,
  // Outdoor
  Tent,
  Sun,
  // Kitchen
  ChefHat,
  UtensilsCrossed,
  // Entertainment
  Tv,
  Wifi,
  Music,
  Gamepad2,
  // Sports
  Bike,
  Ship,
  // Services
  Utensils,
  Shirt,
  Plane,
  // Safety
  ShieldCheck,
} from 'lucide-react';
import { formatCurrency } from '@/lib/currency';
import type { GuestPropertyAmenity } from '@/hooks/useAmenities';
import { ScopeBadge } from './GuestUI';
import { useTranslatedField } from '@/hooks/useTranslatedContent';

// Comprehensive icon mapping for amenities
const ICON_MAP: Record<string, LucideIcon> = {
  // Pool & Wellness
  pool: Waves,
  pool_heated: Thermometer,
  pool_indoor: Building,
  pool_children: Waves,
  hot_tub: Bath,
  sauna: Flame,
  spa: Sparkles,
  // Outdoor
  garden: Trees,
  terrace_shared: Sun,
  courtyard: Trees,
  bbq: Flame,
  barbecue: Flame,
  outdoor_kitchen: ChefHat,
  outdoor_dining: UtensilsCrossed,
  sun_loungers: Sun,
  hammock: Tent,
  fire_pit: Flame,
  playground: Gamepad2,
  // Parking
  parking: Car,
  parking_free: Car,
  parking_paid: Car,
  parking_private: Car,
  parking_garage: Car,
  ev_charger: Car,
  bike_storage: Bike,
  bike_rental: Bike,
  // Beach
  beach_access: Umbrella,
  private_beach: Umbrella,
  beach_equipment: Umbrella,
  kayak: Ship,
  sup_board: Ship,
  boat_dock: Ship,
  // Sports
  tennis: Dumbbell,
  basketball: Dumbbell,
  gym: Dumbbell,
  yoga_space: Dumbbell,
  ping_pong: Gamepad2,
  billiards: Gamepad2,
  mini_golf: Gamepad2,
  // Services
  reception: Coffee,
  concierge: Coffee,
  breakfast: Utensils,
  restaurant: Utensils,
  room_service: Utensils,
  cleaning_daily: Sparkles,
  laundry_service: Shirt,
  airport_shuttle: Plane,
  car_rental: Car,
  // Security
  security_cameras: ShieldCheck,
  gated: Lock,
  security_24h: ShieldCheck,
  intercom: Lock,
  // Accessibility
  wheelchair: Accessibility,
  elevator: Building,
  step_free: Accessibility,
  // Entertainment
  tv: Tv,
  wifi: Wifi,
  streaming: Tv,
  sound_system: Music,
  // Default
  Waves,
  Flame,
  Trees,
  Car,
  Umbrella,
  Dumbbell,
  Coffee,
  Lock,
  Accessibility,
};

interface SharedAmenitiesCardProps {
  amenities: GuestPropertyAmenity[];
  darkMode?: boolean;
  currency?: string;
  /** Property id — used to translate each amenity's note into the guest's language. */
  propertyId?: string;
  /**
   * Whole property rented to one party: nothing is "shared" with strangers, so
   * an unspecified access type means "yours only" (not "Shared").
   */
  wholeProperty?: boolean;
}

/**
 * Renders a property-amenity note, translated into the guest's language. The note
 * is host free-text (operating hours / restrictions / general note) so it must be
 * translated just like unit amenity notes are. Keyed per amenity so each caches
 * independently.
 */
function TranslatedNote({
  note,
  propertyId,
  amenityId,
  darkMode,
}: {
  note: string;
  propertyId?: string;
  amenityId: string;
  darkMode: boolean;
}) {
  const { translated } = useTranslatedField(
    note,
    propertyId || '',
    `property_amenity_note.${amenityId}`,
    'property',
  );
  const text = propertyId ? translated || note : note;
  return (
    <div className={`
      mt-2 text-xs px-2.5 py-1.5 rounded-lg
      ${darkMode ? 'bg-white/5 text-white/70' : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'}
    `}>
      {text}
    </div>
  );
}

type GroupedAmenities = {
  included: GuestPropertyAmenity[];
  withFee: GuestPropertyAmenity[];
  notIncluded: GuestPropertyAmenity[];
};

// Amenities that have dedicated cards in Guest Portal - exclude from SharedAmenitiesCard
const EXCLUDED_AMENITIES = [
  'wifi',           // Has WiFiCard
  'parking_free',   // Has GuestParkingCard
  'parking_paid',   // Has GuestParkingCard
  'parking_private', // Has GuestParkingCard
  'parking_garage', // Has GuestParkingCard
];

export function SharedAmenitiesCard({ amenities, darkMode = false, currency, propertyId, wholeProperty = false }: SharedAmenitiesCardProps) {
  const { t } = useTranslation();
  const [expandedSection, setExpandedSection] = useState<string | null>('included');

  // Filter out amenities that have dedicated cards, then group by access status
  const grouped = useMemo<GroupedAmenities>(() => {
    const result: GroupedAmenities = {
      included: [],
      withFee: [],
      notIncluded: [],
    };

    // Filter out amenities that have their own dedicated cards
    const filteredAmenities = amenities.filter(
      (amenity) => !EXCLUDED_AMENITIES.includes(amenity.amenity_id)
    );

    filteredAmenities.forEach((amenity) => {
      if (amenity.access_status === 'included') {
        result.included.push(amenity);
      } else if (amenity.access_status === 'with_fee') {
        result.withFee.push(amenity);
      } else {
        result.notIncluded.push(amenity);
      }
    });

    return result;
  }, [amenities]);

  // Don't render if no amenities after filtering
  const totalAmenities = grouped.included.length + grouped.withFee.length + grouped.notIncluded.length;
  if (totalAmenities === 0) {
    return null;
  }

  const getIcon = (iconName: string): LucideIcon => ICON_MAP[iconName] || Waves;

  const formatFee = (amenity: GuestPropertyAmenity) => {
    if (!amenity.fee) return '';
    const feeTypeLabel = amenity.fee_type ? t(`amenities.${amenity.fee_type}`) : '';
    return `${formatCurrency(amenity.fee, currency)}${feeTypeLabel ? ` ${feeTypeLabel}` : ''}`;
  };

  // Format note/restrictions for display. The host can set BOTH a free-text note
  // AND structured hours/season/restrictions on the same amenity (the wizard merges
  // them into one card), so show all of them — the note must not swallow the rest.
  const formatNote = (amenity: GuestPropertyAmenity) => {
    const parts: string[] = [];
    if (amenity.notes) {
      parts.push(amenity.notes);
    }
    if (amenity.operating_hours) {
      parts.push(amenity.operating_hours);
    }
    if (amenity.seasonal_period) {
      parts.push(amenity.seasonal_period);
    }
    if (amenity.restrictions) {
      parts.push(amenity.restrictions);
    }
    return parts.join(' • ');
  };

  const renderAmenityItem = (amenity: GuestPropertyAmenity, status: 'included' | 'withFee' | 'notIncluded', index: number) => {
    // Try to get icon by amenity_id first, then by icon field, fallback to Waves
    const Icon = ICON_MAP[amenity.amenity_id] || getIcon(amenity.icon || 'Waves');
    const note = formatNote(amenity);

    return (
      <div
        key={amenity.amenity_id}
        style={{ animationDelay: `${index * 50}ms` }}
        className={`
          flex items-start gap-3 p-3 rounded-xl transition-all duration-200
          animate-in fade-in slide-in-from-left-2
          ${darkMode
            ? 'bg-white/5 hover:bg-white/10 hover:scale-[1.02]'
            : 'bg-gray-50 hover:bg-gray-100 hover:shadow-sm hover:scale-[1.02] dark:bg-gray-800/50 dark:hover:bg-gray-800'
          }
        `}
      >
        <div className={`
          p-2.5 rounded-xl flex-shrink-0 transition-transform duration-200 group-hover:scale-110
          ${status === 'included'
            ? darkMode
              ? 'bg-gradient-to-br from-emerald-500/30 to-teal-500/20 text-emerald-400'
              : 'bg-gradient-to-br from-emerald-100 to-teal-100 text-emerald-600 dark:from-emerald-900/40 dark:to-teal-900/30 dark:text-emerald-400'
            : status === 'withFee'
              ? darkMode
                ? 'bg-gradient-to-br from-amber-500/30 to-orange-500/20 text-amber-400'
                : 'bg-gradient-to-br from-amber-100 to-orange-100 text-amber-600 dark:from-amber-900/40 dark:to-orange-900/30 dark:text-amber-400'
              : darkMode
                ? 'bg-gray-500/20 text-gray-400'
                : 'bg-gray-200 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
          }
        `}>
          <Icon className="h-4 w-4" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className={`
              font-medium text-sm
              ${status === 'notIncluded'
                ? darkMode ? 'text-gray-400 line-through' : 'text-gray-500 line-through dark:text-gray-400'
                : darkMode ? 'text-white' : 'text-gray-900 dark:text-white'
              }
            `}>
              {t(`amenities.items.${amenity.amenity_id}`, amenity.amenity_id)}
            </span>

            {/* Scope badge — always explicit so the guest knows if this is THIS
                unit's or SHARED with the whole property. */}
            {(() => {
              const access = amenity.access_type ?? (wholeProperty ? 'private' : 'shared');
              return (
                <ScopeBadge
                  scope={access === 'private' ? 'unit' : 'shared'}
                  label={
                    access === 'private'
                      ? (wholeProperty ? t('guest.scope.yoursOnly', 'Samo za vas') : t('guest.scope.unitOnly', 'Samo ovaj smještaj'))
                      : access === 'community'
                        ? t('guest.scope.community', 'Cijeli kompleks')
                        : t('guest.scope.shared', 'Zajedničko')
                  }
                  darkMode={darkMode}
                />
              );
            })()}
            {amenity.reservation_required && (
              <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${darkMode ? 'bg-amber-500/20 text-amber-300' : 'bg-amber-100 text-amber-700'}`}>
                {t('guest.amenity.reservationRequired', 'Uz najavu')}
              </span>
            )}
          </div>

          {/* Fee info */}
          {status === 'withFee' && amenity.fee && (
            <div className={`
              inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-full text-xs font-medium
              ${darkMode
                ? 'bg-amber-500/20 text-amber-300'
                : 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400'
              }
            `}>
              <Euro className="h-3 w-3" />
              {formatFee(amenity)}
            </div>
          )}

          {/* Note - shown nicely, translated into the guest's language */}
          {note && status !== 'notIncluded' && (
            <TranslatedNote note={note} propertyId={propertyId} amenityId={amenity.amenity_id} darkMode={darkMode} />
          )}
        </div>

        {/* Status indicator */}
        <div className={`
          flex-shrink-0 p-1 rounded-full
          ${status === 'included'
            ? darkMode ? 'bg-emerald-500/20' : 'bg-emerald-100 dark:bg-emerald-900/30'
            : status === 'withFee'
              ? darkMode ? 'bg-amber-500/20' : 'bg-amber-100 dark:bg-amber-900/30'
              : ''
          }
        `}>
          {status === 'included' && (
            <Check className={`h-4 w-4 ${darkMode ? 'text-emerald-400' : 'text-emerald-500 dark:text-emerald-400'}`} />
          )}
          {status === 'withFee' && (
            <Euro className={`h-4 w-4 ${darkMode ? 'text-amber-400' : 'text-amber-500 dark:text-amber-400'}`} />
          )}
          {status === 'notIncluded' && (
            <X className={`h-4 w-4 ${darkMode ? 'text-gray-500' : 'text-gray-400 dark:text-gray-500'}`} />
          )}
        </div>
      </div>
    );
  };

  const renderSection = (
    title: string,
    items: GuestPropertyAmenity[],
    sectionKey: string,
    status: 'included' | 'withFee' | 'notIncluded',
    icon: React.ReactNode,
    color: string
  ) => {
    if (items.length === 0) return null;

    const isExpanded = expandedSection === sectionKey;

    return (
      <div className="overflow-hidden">
        <button
          type="button"
          onClick={() => setExpandedSection(isExpanded ? null : sectionKey)}
          className={`
            w-full flex items-center justify-between p-3 transition-colors
            ${darkMode
              ? 'hover:bg-white/5'
              : 'hover:bg-gray-50 dark:hover:bg-gray-800/50'
            }
          `}
        >
          <div className="flex items-center gap-2">
            <div className={`p-1.5 rounded-lg ${color}`}>
              {icon}
            </div>
            <span className={`font-medium text-sm ${darkMode ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
              {title}
            </span>
            <span className={`
              text-xs px-2 py-0.5 rounded-full
              ${darkMode
                ? 'bg-white/10 text-white/70'
                : 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
              }
            `}>
              {items.length}
            </span>
          </div>
          {isExpanded ? (
            <ChevronUp className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          ) : (
            <ChevronDown className={`h-4 w-4 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`} />
          )}
        </button>

        {isExpanded && (
          <div className="px-3 pb-3 space-y-2 animate-in slide-in-from-top-2 duration-200">
            {items.map((amenity, index) => renderAmenityItem(amenity, status, index))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`
      rounded-2xl overflow-hidden
      ${darkMode
        ? 'bg-slate-800 border border-slate-700 shadow-xl shadow-black/40 ring-1 ring-white/5'
        : 'bg-white border border-emerald-100/40 shadow-lg shadow-gray-200/80'
      }
    `}>
      {/* Header */}
      <div className={`
        p-4 border-b
        ${darkMode
          ? 'border-slate-700/50'
          : 'border-gray-200'
        }
      `}>
        <div className="flex items-center gap-3">
          <div>
            <h3 className={`font-bold text-lg tracking-tight ${darkMode ? 'text-white' : 'text-gray-900 dark:text-white'}`}>
              {wholeProperty ? t('amenities.propertyAmenitiesWhole', 'Sadržaji imanja') : t('amenities.propertyAmenities')}
            </h3>
            <p className={`text-xs ${darkMode ? 'text-white/60' : 'text-gray-500 dark:text-gray-400'}`}>
              {wholeProperty ? t('amenities.wholePropertyFacilities', 'Sve je samo za vaše društvo') : t('amenities.sharedFacilities')}
            </p>
          </div>
        </div>
      </div>

      {/* Sections */}
      <div className={`divide-y ${darkMode ? 'divide-slate-700/50' : 'divide-gray-200'}`}>
        {renderSection(
          t('amenities.accessStatus.included'),
          grouped.included,
          'included',
          'included',
          <Check className="h-4 w-4 text-white" />,
          darkMode ? 'bg-emerald-500/80' : 'bg-emerald-500'
        )}

        {renderSection(
          t('amenities.accessStatus.withFee'),
          grouped.withFee,
          'withFee',
          'withFee',
          <Euro className="h-4 w-4 text-white" />,
          darkMode ? 'bg-amber-500/80' : 'bg-amber-500'
        )}

        {renderSection(
          t('amenities.accessStatus.notAvailable'),
          grouped.notIncluded,
          'notIncluded',
          'notIncluded',
          <X className="h-4 w-4 text-white" />,
          darkMode ? 'bg-gray-500/80' : 'bg-gray-500'
        )}
      </div>

      {/* Footer hint */}
      {grouped.withFee.length > 0 && (
        <div className={`
          px-4 py-3 text-xs
          ${darkMode
            ? 'bg-slate-700/30 text-slate-400'
            : 'bg-gray-50 text-gray-500'
          }
        `}>
          {t('amenities.feeHint')}
        </div>
      )}
    </div>
  );
}

export default SharedAmenitiesCard;
